import * as ol from 'openlayers';
import {Map, View} from 'openlayers';
import Size = ol.Size;
import {ChannelMarker} from './channel-marker';
import Projection = ol.proj.Projection;
import Extent = ol.Extent;
import Coordinate = ol.Coordinate;
import {DrawingFeature} from './drawing-feature';
import Vector = ol.source.Vector;
import {Channel} from '../channel.model';
import {HighlightedChannelMarker} from './highlighted-channel-marker';
import Feature = ol.Feature;

export class MapDrawing extends Map {
  public static readonly COORDINATE_SCALE = 1.0;
  public static readonly CHANNEL_SELECTED = 'channelSelected';
  public static readonly COORDINATE_SELECTED = 'coordinateSelected';

  public static WIDTH = 600;
  public static HEIGHT = 480;

  private view: View;
  private projection: Projection;
  private projectionExtent: Extent;

  private location: string;
  private worldExtent: Extent;
  private imageExtent: Extent;
  private centerOffset: Coordinate;
  private center: Coordinate;
  private zoom: number;
  private context: CanvasRenderingContext2D;

  private drawingImageSource: ol.source.ImageStatic;
  private drawingLayer: ol.layer.Vector;
  private image: ol.layer.Image;

  private channelVectorSource: ol.source.Vector;
  private channelLayer: ol.layer.Vector;

  private highlightedChannelVectorSource: ol.source.Vector;
  private highlightedChannelLayer: ol.layer.Vector;

  private drawingFeature: DrawingFeature;

  private channels: Array<Channel> = [];
  private channelMarkers: Array<ChannelMarker> = [];
  private highlightedChannel: HighlightedChannelMarker;
  private viewMode: boolean = false;

  constructor(options: any, width: number, height: number) {
    super(options);

    if(typeof options.viewMode !== 'undefined') {
      this.viewMode = options.viewMode;
    }
    this.projectionExtent = [0, 0, width, height];
    this.worldExtent = [0, 0, MapDrawing.WIDTH, MapDrawing.HEIGHT];
    this.imageExtent = [0, 0, width, height];
    this.center = ol.extent.getCenter(this.worldExtent);

    this.zoom = 3;

    this.once('precompose', this.onPreCompose, this);
    this.once('postcompose', this.onPostCompose, this);

    this.view = new View({
      center: this.center,
      zoom: this.zoom,
      enableRotation: false,
      extent: this.imageExtent,
      minZoom: 14,
      maxZoom: 19,
    });

    this.projection = new Projection({
      extent: this.imageExtent,
      worldExtent: this.worldExtent,
      units: 'pixels',
      code: 'xkcd-image'
    });

    if(!this.viewMode) {
      this.on('click', this.onClick, this);
    }

    this.initLayers();
    this.setSize([MapDrawing.WIDTH, MapDrawing.HEIGHT]);
    this.setView(this.view);

  }

  public loadChannels(channels: Channel[]) {
    for(let i = 0; i < channels.length; i++) {
      const channel = channels[i];

      if(channel.coordinate) {
        const channelMarker = new ChannelMarker(channel.coordinate[0], channel.coordinate[1], channel.location, channel.label, MapDrawing.COORDINATE_SCALE);
        channelMarker.channel = channel;
        this.channelVectorSource.addFeature(channelMarker);
        this.channelMarkers.push(channelMarker);
        this.channels.push(channel);
      }
    }
  }

  public applyHighlightedChannelPosition(coordinate: [number, number]) {
    if(!this.highlightedChannel) {
      this.setHighlightedChannelPosition(coordinate, this.location);
    }
    this.highlightedChannel.getGeometry()['setCoordinates']([coordinate[0] * MapDrawing.COORDINATE_SCALE, coordinate[1] * MapDrawing.COORDINATE_SCALE]);
  }

  public setHighlightedChannelPosition(coordinate: [number, number], location: string) {
    this.location = location || null;
    if(this.highlightedChannel === undefined) {
      this.highlightedChannel = new HighlightedChannelMarker(coordinate[0], coordinate[1], location, MapDrawing.COORDINATE_SCALE);
      this.highlightedChannelVectorSource.addFeature(this.highlightedChannel);
    }
    this.highlightedChannel.getGeometry()['setCoordinates']([coordinate[0], coordinate[1]]);
  }

  private onClick(event) {
    const px = event.coordinate[0];
    const py = event.coordinate[1];
    if (!(px > this.imageExtent[0] &&
          px < this.imageExtent[2] &&
          py > this.imageExtent[1] &&
          py < this.imageExtent[3] )) {
      return;
    }
    let channelSelected = false;

    let nearest = Number.MAX_VALUE;
    let nearestChannelMarker: ChannelMarker = null;
    for(let i = 0; i < this.channelMarkers.length; i++) {
      const ft: ChannelMarker = <ChannelMarker>this.channelMarkers[i];
      const dx = px - ft.x * MapDrawing.COORDINATE_SCALE;
      const dy = py - ft.y * MapDrawing.COORDINATE_SCALE;
      const delta = Math.sqrt(dx * dx + dy * dy);
      if (delta < 8 * this.view.getZoom() && delta < nearest) {
        nearest = delta;
        nearestChannelMarker = ft;
        channelSelected = true;
      }
    }

    if(this.highlightedChannel === undefined) {
      this.highlightedChannel = new HighlightedChannelMarker(px, py, this.location, MapDrawing.COORDINATE_SCALE);
      this.highlightedChannelVectorSource.addFeature(this.highlightedChannel);
    }
    this.highlightedChannel.getGeometry()['setCoordinates']([px, py]);

    if(!channelSelected) {
      this.dispatchEvent(new CustomEvent(MapDrawing.CHANNEL_SELECTED, {detail: null}));
    } else {
      this.dispatchEvent(new CustomEvent(MapDrawing.CHANNEL_SELECTED, <CustomEventInit>{detail: nearestChannelMarker}));
    }
    this.dispatchEvent(new CustomEvent(MapDrawing.COORDINATE_SELECTED, {detail: [px, py]}));
  }


  private onPreCompose(event: ol.render.Event) {

  }

  private onPostCompose(event: ol.render.Event) {
    this.context = event.context;
    this.view.setCenter(ol.extent.getCenter(this.projectionExtent));
    this.view.fit(this.projectionExtent);
  }

  private initLayers(): void {

    this.image = new ol.layer.Image();

    this.channelVectorSource = new ol.source.Vector();
    this.channelLayer = new ol.layer.Vector();
    this.channelLayer.setSource(this.channelVectorSource);

    this.highlightedChannelVectorSource = new ol.source.Vector();
    this.highlightedChannelLayer = new ol.layer.Vector();
    this.highlightedChannelLayer.setSource(this.highlightedChannelVectorSource);

    this.addLayer(this.image);
    this.addLayer(this.channelLayer);
    this.addLayer(this.highlightedChannelLayer);
  }

  public loadImage(url: string): void {

    this.drawingImageSource = new ol.source.ImageStatic({
      url: url,
      projection: this.projection,
      imageExtent: this.imageExtent,
      crossOrigin: 'anonymous',
      imageLoadFunction: (image, src) => {
        image.getImage()['src'] = src;
        const img = image.getImage();
        img['onload'] = () => {
          this.image.setExtent([0, 0, img['width'], img['height']]);
        };
      }
    });

    this.image.setSource(this.drawingImageSource);
  }
}



// WEBPACK FOOTER //
// /var/jenkins_home/workspace/brands-compart-portal-frontend/checkout/src/app/components/channel/drawing/drawing.ts